import React, { createContext, useContext, useEffect, useState } from 'react'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'

import { Message, MessageSeverity } from 'constants/message'

import { useLocation, useNavigate } from 'react-router-dom'
import ModalLogIn from 'components/Modal/modalLogIn'
import LangContext from './LangContext'
import LightDarkContext from './LightDarkModeContext'
import MessageContext from './MessageContext'
import i18n from 'i18n'

type AuthContext = {
  userName: string
  isMfaActivated: boolean
  isSsoActivated: boolean
  isConnected: boolean
  setIsConnected: React.Dispatch<React.SetStateAction<boolean>>
  handleOpenModalLogIn: () => void
  handleDisconnect: () => void
  handleRequestLogin: () => void
}

const AuthContext = createContext({
  userName: '',
  isMfaActivated: false,
  isSsoActivated: false,
  setIsConnected: (b: boolean) => {},
  isConnected: false,
  setModalLogInOpened: (b: boolean) => {},
  handleOpenModalLogIn: () => {},
  handleDisconnect: () => {},
  handleRequestLogin: () => {},
})

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const baseUrl = process.env.PUBLIC_URL || ''
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { lang, handleSwitchLanguage } = useContext(LangContext)
  const { setMode } = useContext(LightDarkContext)
  const { handleDisplayMessage, handleClearMessages } = useContext(MessageContext)

  const [modalLogInOpened, setModalLogInOpened] = useState(LocalStorage.user ? true : false)

  const [forceLogIn, setForceLogIn] = useState(
    !(LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined) ? true : false),
  )
  const [userName, setUserName] = useState(
    LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined),
  )

  const isMfaActivated = eval('CONFIGAPP__MFA_ACTIVATED') || false
  const isSsoActivated = eval('CONFIGAPP__SSO_ACTIVATED') || false

  const [isConnected, setIsConnected] = useState(
    LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined) ? true : false,
  )

  const handleOpenModalLogIn = () => {
    setModalLogInOpened(true)
  }
  const handleCloseModalLogIn = () => {
    setModalLogInOpened(false)
  }

  const handleRequestLogin = () => {
    setForceLogIn(true)
  }

  const handleUserConnected = (isUserConnected: boolean) => {
    setIsConnected(isUserConnected)
  }

  const handlePostLoggedIn = () => {
    setUserName(LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined))
    setIsConnected(true)
    setModalLogInOpened(false)
    handleSwitchLanguage(
      LocalStorage.getOrDefault(
        [LocalStorage.KEY_PREFS, ComponentsUNames.USERPREFS_STORE_NAME, ComponentsUNames.USERPREFS_LANG],
        LocalStorage.VALUE_TYPE.STRING,
        'fr',
      ),
    )
    setMode(
      LocalStorage.getOrDefault(
        [LocalStorage.KEY_PREFS, ComponentsUNames.USERPREFS_STORE_NAME, ComponentsUNames.USERPREFS_THEME],
        LocalStorage.VALUE_TYPE.STRING,
        'light',
      ),
    )

    handleDisplayMessage(new Message(MessageSeverity.INFO, '', null, i18n.t('9500132')))

    setForceLogIn(false)

    handleUserConnected(true)
  }

  const handleDisconnect = () => {
    // Memorize IHM-language only in localStorage
    const keyForLang = LocalStorage.KEY_LANG
    const lang = LocalStorage.getOrDefault(keyForLang, LocalStorage.VALUE_TYPE.STRING, 'fr')
    LocalStorage.clear()
    LocalStorage.put(keyForLang, lang)
    handleClearMessages()
    setIsConnected(false)
    // Go back to home page
    navigate(baseUrl + '/')
  }

  const handleCancelLogInModal = () => {
    setModalLogInOpened(false)
    setIsConnected(false)
  }

  useEffect(() => {
    // On page opening, free your body
    document.getElementsByTagName('body')[0].style['overflow'] = 'auto'
    // Scroll to top of the page since the user may have scrolled down in previous page
    document.getElementById('root')?.scrollIntoView({ behavior: 'instant' })
    // Check session is still active
    const timestampLastServerCall = LocalStorage.getOrDefault(
      [LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL],
      LocalStorage.VALUE_TYPE.STRING,
      undefined,
    )
    const hasSessionTimedOut = timestampLastServerCall
      ? new Date().getTime() - timestampLastServerCall > 20 * 60 * 1000
      : true
    if (hasSessionTimedOut) {
      setIsConnected(false)
    }
    // Set-up language
    handleSwitchLanguage(lang)
  }, [pathname])

  useEffect(() => {
    if (forceLogIn || !isConnected) {
      handleOpenModalLogIn()
    }
  }, [forceLogIn, isConnected])

  return (
    <>
      <AuthContext.Provider
        value={{
          userName,
          isMfaActivated,
          isSsoActivated,
          setIsConnected,
          isConnected,
          setModalLogInOpened,
          handleOpenModalLogIn,
          handleDisconnect,
          handleRequestLogin,
        }}
      >
        {children}
        <ModalLogIn
          lang={lang}
          isOpened={modalLogInOpened}
          onLoggedIn={handlePostLoggedIn}
          onCloseModal={handleCloseModalLogIn}
          onCancelModal={handleCancelLogInModal}
          onDisplayMessage={handleDisplayMessage}
        />
      </AuthContext.Provider>
    </>
  )
}

export default AuthContext
